/**
 * Allowed Courses - Cert Body View
 * Display courses that the cert body is allowed to assign
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

function AllowedCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllowedCourses();
  }, []);

  const fetchAllowedCourses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE}/cert-body/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Only certification bodies can access this page');
        }
        throw new Error('Failed to fetch allowed courses');
      }

      const data = await response.json();
      setCourses(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching allowed courses:', err);
      setError(err.message || 'Failed to load courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '24px'
  };

  const theadStyle = {
    background: '#f9f9f9',
    borderBottom: '2px solid #d4af37'
  };

  const thStyle = {
    padding: '16px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#1a1a1a',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const tdStyle = {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
    fontSize: '0.95rem'
  };

  const badgeStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    background: '#d4af37',
    color: '#1a1a1a'
  };

  const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    color: '#666'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Allowed Courses</h1>
        </div>
        <div style={emptyStateStyle}>Loading courses...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>Allowed Courses</h1>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            Courses you are authorized to assign to your clients
          </p>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {courses.length === 0 ? (
        <div style={emptyStateStyle}>
          <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>No courses available</p>
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
            Contact the system administrator to get courses assigned to your certification body
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '16px', color: '#666', fontSize: '0.95rem' }}>
            Showing {courses.length} course(s)
          </div>
          <div style={{ overflowX: 'auto', border: '2px solid #d4af37', borderRadius: '8px' }}>
            <table style={tableStyle}>
              <thead style={theadStyle}>
                <tr>
                  <th style={thStyle}>Course Title</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Certificate Type</th>
                  <th style={thStyle}>Modules</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Allowed Date</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id}>
                    <td style={tdStyle}>
                      <strong style={{ color: '#1a1a1a' }}>{course.title}</strong>
                      {course.description && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                          {course.description.substring(0, 100)}
                          {course.description.length > 100 ? '...' : ''}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>{course.category_name || 'Uncategorized'}</td>
                    <td style={tdStyle}>
                      <span style={{ textTransform: 'uppercase', fontWeight: '600', color: '#d4af37' }}>
                        {course.certificate_type}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '700' }}>
                        {course.module_count || 0}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={badgeStyle}>
                        {course.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {new Date(course.allowed_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default AllowedCourses;
